'use client'

import * as React from 'react'

type Theme = 'dark' | 'light' | 'system'

type ThemeProviderProps = {
  children: React.ReactNode
  defaultTheme?: Theme
  storageKey?: string
  attribute?: 'class' | 'data-theme'
  enableSystem?: boolean
  disableTransitionOnChange?: boolean
}

type ThemeProviderState = {
  theme: Theme
  setTheme: (theme: Theme) => void
}

const ThemeProviderContext = React.createContext<ThemeProviderState | undefined>(
  undefined
)

export function ThemeProvider({
  children,
  defaultTheme = 'light',
  storageKey = 'manufacturing-guide-theme',
  attribute = 'class',
  enableSystem = true,
  disableTransitionOnChange = false,
  ...props
}: ThemeProviderProps) {
  const [theme, setTheme] = React.useState<Theme>(
    () => (localStorage.getItem(storageKey) as Theme) || defaultTheme
  )

  // Handle system theme changes
  React.useEffect(() => {
    if (enableSystem && theme === 'system') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
      
      const updateSystemTheme = (e: MediaQueryListEvent | MediaQueryList) => {
        const root = window.document.documentElement
        const systemTheme = e.matches ? 'dark' : 'light'
        
        if (disableTransitionOnChange) {
          root.classList.add('[&_*]:!transition-none')
        }

        if (attribute === 'class') {
          root.classList.remove('light', 'dark')
          root.classList.add(systemTheme)
        } else {
          root.setAttribute('data-theme', systemTheme)
        }

        if (disableTransitionOnChange) {
          // Force a reflow
          window.document.body.clientHeight
          root.classList.remove('[&_*]:!transition-none')
        }
      }

      // Initial check
      updateSystemTheme(mediaQuery)

      // Listen for system theme changes
      mediaQuery.addEventListener('change', updateSystemTheme)
      return () => mediaQuery.removeEventListener('change', updateSystemTheme)
    }
  }, [theme, enableSystem, disableTransitionOnChange, attribute])

  // Handle theme changes
  React.useEffect(() => {
    const root = window.document.documentElement

    if (disableTransitionOnChange) {
      root.classList.add('[&_*]:!transition-none')
    }

    if (theme === 'system') {
      if (!enableSystem) {
        // If system is disabled, fall back to light
        if (attribute === 'class') {
          root.classList.remove('dark')
          root.classList.add('light')
        } else {
          root.setAttribute('data-theme', 'light')
        }
      }
      return // System theme is handled by the other useEffect
    }

    if (attribute === 'class') {
      root.classList.remove('light', 'dark')
      root.classList.add(theme)
    } else {
      root.setAttribute('data-theme', theme)
    }

    if (disableTransitionOnChange) {
      // Force a reflow
      window.document.body.clientHeight
      root.classList.remove('[&_*]:!transition-none')
    }
  }, [theme, enableSystem, disableTransitionOnChange, attribute])

  const value = React.useMemo(
    () => ({
      theme,
      setTheme: (theme: Theme) => {
        localStorage.setItem(storageKey, theme)
        setTheme(theme)
      },
    }),
    [theme, storageKey]
  )

  return (
    <ThemeProviderContext.Provider {...props} value={value}>
      {children}
    </ThemeProviderContext.Provider>
  )
}

export const useTheme = () => {
  const context = React.useContext(ThemeProviderContext)

  if (context === undefined)
    throw new Error('useTheme must be used within a ThemeProvider')

  return context
}
